import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, AlertCircle, Download, Copy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { toast } from "sonner";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2500;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed | expired | error
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      setError("Session manquante.");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data: resp } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(resp);
        if (resp.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (resp.status === "expired") {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
        setState("error");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const copyKey = () => {
    if (!data?.license_key) return;
    navigator.clipboard.writeText(data.license_key);
    toast.success("Clé copiée");
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="checkout-success-page">
      <PublicHeader />
      <section className="max-w-2xl mx-auto px-6 py-20">
        {state === "polling" && (
          <div className="text-center" data-testid="checkout-polling">
            <Loader2 className="h-12 w-12 mx-auto text-primary animate-spin mb-4" />
            <h1 className="text-2xl font-bold mb-2">Vérification du paiement…</h1>
            <p className="text-muted-foreground text-sm">Quelques secondes, ne fermez pas cette page.</p>
          </div>
        )}

        {state === "paid" && (
          <div className="bg-card border-2 border-primary rounded-2xl p-10 text-center" data-testid="checkout-paid">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-3xl font-black tracking-tight mb-3">Paiement confirmé 🎉</h1>
            <p className="text-muted-foreground mb-8">
              Merci pour votre achat. Voici votre clé de licence WiLink&nbsp;:
            </p>

            {data?.license_key ? (
              <div className="bg-foreground text-background rounded-xl p-6 mb-6" data-testid="license-key-box">
                <p className="text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">Votre clé de licence</p>
                <p className="font-mono text-lg font-bold tracking-wider break-all">{data.license_key}</p>
                <button
                  onClick={copyKey}
                  className="mt-4 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-background/30 hover:bg-background/10"
                  data-testid="copy-license-btn"
                >
                  <Copy className="h-3 w-3" /> Copier
                </button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-6">
                Votre licence est en cours de génération. Vous la recevrez par email d&apos;ici quelques instants.
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/wilink-php.zip"
                className="h-12 px-6 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium inline-flex items-center justify-center gap-2"
                data-testid="download-zip-btn"
              >
                <Download className="h-4 w-4" /> Télécharger WiLink (.zip)
              </a>
              <Link
                to="/"
                className="h-12 px-6 rounded-md border border-border hover:bg-accent font-medium inline-flex items-center justify-center"
              >
                Retour à l&apos;accueil
              </Link>
            </div>

            <p className="text-xs text-muted-foreground mt-8">
              Un récapitulatif a été envoyé à votre email. Conservez votre clé de licence&nbsp;: elle vous sera demandée lors de l&apos;installation.
            </p>
          </div>
        )}

        {state === "expired" && (
          <div className="bg-card border border-border rounded-xl p-10 text-center" data-testid="checkout-expired">
            <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-3">Session expirée</h1>
            <p className="text-muted-foreground mb-6">La session de paiement a expiré. Vous pouvez recommencer.</p>
            <Link to="/tarifs" className="text-primary hover:underline">← Retour aux tarifs</Link>
          </div>
        )}

        {state === "failed" && (
          <div className="bg-card border border-border rounded-xl p-10 text-center" data-testid="checkout-failed">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-3">Statut indisponible</h1>
            <p className="text-muted-foreground mb-6">
              Nous n&apos;avons pas pu confirmer le paiement après plusieurs tentatives. Si le débit est effectif, vérifiez votre boîte mail — votre clé arrivera sous peu.
            </p>
            <Link to="/contact" className="text-primary hover:underline">Contactez le support →</Link>
          </div>
        )}

        {state === "error" && (
          <div className="bg-card border border-border rounded-xl p-10 text-center" data-testid="checkout-error">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-3">Une erreur est survenue</h1>
            <p className="text-muted-foreground mb-6">{error || "Erreur inconnue"}</p>
            <Link to="/tarifs" className="text-primary hover:underline">← Retour aux tarifs</Link>
          </div>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}
